import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * URL state persistence for Division 26 Electrical panel views & modals.
 * Query params: d26View, d26PanelId, d26Modal, d26Review
 */
export function useElectricalUrlState() {
  const [searchParams, setSearchParams] = useSearchParams();

  const d26View = searchParams.get('d26View');
  const d26PanelId = searchParams.get('d26PanelId');
  const d26Modal = searchParams.get('d26Modal');
  const d26Review = searchParams.get('d26Review');
  const d26Edit = searchParams.get('d26Edit');

  const setScheduleView = useCallback((panelId) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('d26View', 'schedule');
      next.set('d26PanelId', panelId);
      next.delete('d26Modal');
      next.delete('d26Review');
      return next;
    });
  }, [setSearchParams]);

  const setEditModal = useCallback((panelId) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('d26Modal', 'edit');
      next.set('d26PanelId', panelId);
      return next;
    });
  }, [setSearchParams]);

  const setReviewOpen = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('d26Review', 'open');
      return next;
    });
  }, [setSearchParams]);

  const clearReview = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('d26Review');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const clearScheduleView = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('d26View');
      next.delete('d26PanelId');
      next.delete('d26Review');
      next.delete('d26Edit');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const clearEditModal = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('d26Modal');
      if (!next.get('d26View')) next.delete('d26PanelId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setScheduleEdit = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('d26Edit', 'true');
      return next;
    });
  }, [setSearchParams]);

  const clearScheduleEdit = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('d26Edit');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return {
    urlView: d26View,
    urlPanelId: d26PanelId,
    urlModal: d26Modal,
    urlReview: d26Review,
    urlEdit: d26Edit,
    setScheduleView,
    setEditModal,
    setReviewOpen,
    clearReview,
    clearScheduleView,
    clearEditModal,
    setScheduleEdit,
    clearScheduleEdit,
  };
}
